const FREE_SHIPPING_FALLBACK_CENTS = 9500;
const SUBSCRIPTION_SHIPS_FREE_FALLBACK = true;

const readShippingConfig = (root) => {
  const el =
    root?.querySelector?.('[data-dw-free-shipping-threshold]') ||
    document.querySelector('[data-dw-free-shipping-threshold]');

  const cents = parseInt(el?.dataset.dwFreeShippingThreshold, 10);
  const subsFree = el?.dataset.dwSubscriptionShipsFree;

  return {
    thresholdCents: Number.isFinite(cents) && cents > 0 ? cents : FREE_SHIPPING_FALLBACK_CENTS,
    subscriptionShipsFree:
      subsFree === undefined ? SUBSCRIPTION_SHIPS_FREE_FALLBACK : subsFree === 'true',
  };
};

const formatMoney = (cents, currency) =>
  (cents / 100).toLocaleString('en-AU', { style: 'currency', currency });


const updateCartCount = async () => {
  const cart = await fetch('/cart.js').then((res) => res.json());
  const count = cart.item_count;
  document.querySelectorAll('.dw-header__cart').forEach((cartButton) => {
    let badge = cartButton.querySelector('.dw-header__cart-count');
    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'dw-header__cart-count';
        cartButton.appendChild(badge);
      }
      badge.textContent = count;
      badge.style.display = 'flex';
    } else if (badge) {
      badge.style.display = 'none';
    }
  });
};

// A subscription line's `original_line_price` equals its `final_line_price` —
// Shopify does not express the pre-discount figure there. The only real
// compare price lives on the selling plan allocation. This is the one Shopify
// quirk from the old file worth carrying forward; without it a subscription
// line silently loses its strikethrough.
const realOriginalLinePrice = (item) => {
  const compareAtPrice = item.selling_plan_allocation?.compare_at_price;
  if (compareAtPrice && compareAtPrice > item.final_price) {
    return compareAtPrice * item.quantity;
  }
  return item.original_line_price;
};

const updateSubtotalDisplay = (cart) => {
  const compareTotal = cart.items.reduce((sum, item) => sum + realOriginalLinePrice(item), 0);

  document.querySelectorAll('.dw-cart-empty__subtotal-prices').forEach((wrap) => {
    let priceEl = wrap.querySelector('p');
    if (!priceEl) {
      priceEl = document.createElement('p');
      wrap.insertBefore(priceEl, wrap.firstChild);
    }
    priceEl.textContent = formatMoney(cart.total_price, cart.currency);

    let compareEl = wrap.querySelector('.dw-cart-empty__subtotal-compare');
    if (compareTotal > cart.total_price) {
      if (!compareEl) {
        compareEl = document.createElement('s');
        compareEl.className = 'dw-cart-empty__subtotal-compare';
        wrap.appendChild(compareEl);
      }
      compareEl.textContent = formatMoney(compareTotal, cart.currency);
    } else if (compareEl) {
      compareEl.remove();
    }
  });
};

// ─── Line identity ──────────────────────────────────────────
//
// A cart line is now fully described by its variant and its selling plan.
// Two lines that match on both are the same purchase, and Shopify merges them
// itself — which is the behaviour we want, so nothing here fights it.
const lineKeyFor = (item) =>
  `${item.variant_id}:${item.selling_plan_allocation?.selling_plan?.id ?? 'onetime'}`;

// ─── Subscription quantity cap ──────────────────────────────
//
// One subscription per line; + is disabled, - and remove still work. Read off
// the row's <select> so it stays correct through an in-cart change without
// needing a separate flag.
const isSubscriptionRow = (row) => {
  const select = row?.querySelector('[data-dw-frequency-select]');
  return !!select && select.value.startsWith('subscribe:');
};

const syncQuantityPlusState = (row) => {
  const plusBtn = row?.querySelector('[data-quantity-plus]');
  if (!plusBtn) return;
  const capped = isSubscriptionRow(row);
  plusBtn.disabled = capped;
  plusBtn.title = capped ? 'Subscriptions are limited to 1 per order' : '';
};

const syncAllQuantityPlusStatesIn = (root) => {
  root.querySelectorAll('[data-line]').forEach(syncQuantityPlusState);
};

// ─── Free shipping bar ──────────────────────────────────────

const shippingProgressFor = (cart, config) => {
  const earnedBySubscription =
    config.subscriptionShipsFree && cart.items.some((item) => item.selling_plan_allocation);
  const remaining = Math.max(config.thresholdCents - cart.total_price, 0);
  const earned = earnedBySubscription || remaining === 0;

  return {
    earned,
    remaining,
    // Never show a sliver of nothing: an almost-empty cart still gets a
    // visible nub so the bar reads as a progress track rather than an
    // empty box someone forgot to fill.
    percent: earned
      ? 100
      : Math.max(4, Math.round((cart.total_price / config.thresholdCents) * 100)),
  };
};

// ONE BAR, KEPT ALIVE.
//
// The first version built the bar inside the freshly-parsed (detached) drawer
// on every render, which made it a brand-new element every time. A brand-new
// element has no previous width to transition from, so the fill could only
// ever jump — and the deferred "now animate to the real number" step never
// landed, leaving the bar permanently one render behind: it showed the
// PREVIOUS cart's percentage while the text showed the current one.
//
// Instead the live bar element is now carried across the content swap (see
// applyDrawerContent) and updated in place afterwards. The browser has
// genuinely rendered it at the old width, so `transition: width` in the CSS
// does the animation by itself, with no frame-timing tricks at all.
function removeShippingBarGracefully(bar) {
  if (!bar || bar.dataset.dwLeaving === 'true') return;
  bar.dataset.dwLeaving = 'true';

  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    bar.remove();
  };

  const scheduleFrame =
    typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (cb) => setTimeout(cb, 16);

  void bar.offsetHeight;

  scheduleFrame(() => {
    scheduleFrame(() => {
      if (!bar.isConnected) {
        finish();
        return;
      }
      void bar.offsetHeight;
      bar.classList.add('is-leaving');
      bar.addEventListener('transitionend', finish);
      setTimeout(finish, 500); // fallback; matches the 280ms CSS with headroom
    });
  });
}

// Always call this on the LIVE drawer, never on a detached copy — the whole
// animation depends on the browser having painted the bar at its old width.
function syncShippingProgressBar(root, cart) {
  const existingBar = root.querySelector('[data-dw-shipping-bar]');

  // An empty cart has no progress to show.
  if (!cart || cart.item_count === 0) {
    if (existingBar) removeShippingBarGracefully(existingBar);
    return;
  }

  const { earned, remaining, percent } = shippingProgressFor(cart, readShippingConfig(root));

  let bar = existingBar;
  const isNewBar = !bar;

  if (bar && bar.dataset.dwLeaving === 'true') {
    delete bar.dataset.dwLeaving;
    bar.classList.remove('is-leaving');
  }

  if (!bar) {
    bar = document.createElement('div');
    bar.className = 'dw-shipping-bar';
    bar.setAttribute('data-dw-shipping-bar', '');
    bar.innerHTML =
      '<p class="dw-shipping-bar__text"></p>' +
      '<div class="dw-shipping-bar__track">' +
      '<div class="dw-shipping-bar__fill"></div>' +
      '</div>';

    const header = root.querySelector('.cart-drawer__header');
    if (header) {
      header.insertAdjacentElement('afterend', bar);
    } else {
      const content = root.querySelector('.cart-drawer__content');
      if (content) content.insertAdjacentElement('beforebegin', bar);
    }
  }

  bar.classList.toggle('is-earned', earned);

  const textEl = bar.querySelector('.dw-shipping-bar__text');
  if (textEl) {
    textEl.innerHTML = earned
      ? 'You get <strong>FREE</strong> shipping'
      : `You're <strong>${formatMoney(remaining, cart.currency)}</strong> away from free shipping`;
  }

  const fill = bar.querySelector('.dw-shipping-bar__fill');
  if (!fill) return;

  // A bar that's just been created starts empty and needs one forced layout so
  // the browser commits 0% before the real width is applied — otherwise it
  // coalesces both into a single style recalc and the fill simply appears.
  // An existing bar already has a painted width, so it transitions on its own.
  if (isNewBar) {
    fill.style.width = '0%';
    void fill.offsetWidth;
  }

  fill.style.width = `${percent}%`;
}

// What a freshly-parsed, not-yet-attached drawer needs before it's shown.
// The shipping bar is deliberately NOT here — it's synced after the swap, on
// the live element, so its width can animate. Nothing here touches a line's
// label, price or quantity; those come server-rendered and correct.
const syncDrawerState = (root) => {
  syncAllQuantityPlusStatesIn(root);
};

// ─── Drawer open / close / re-render ────────────────────────

const getDrawer = () => document.querySelector('[data-dw-cart-overlay]');

const openCart = () => {
  const drawer = getDrawer();
  if (!drawer) return;
  if (!drawer.open) drawer.showModal(); // showModal() on an open dialog throws
  document.documentElement.classList.add('dw-cart-is-open');
};

const closeCart = () => {
  const drawer = getDrawer();
  if (!drawer) return;
  drawer.classList.add('is-closing');
  window.setTimeout(() => {
    drawer.close();
    drawer.classList.remove('is-closing');
    document.documentElement.classList.remove('dw-cart-is-open');
  }, 260);
};

// Copies fresh markup INTO the existing dialog rather than replacing the
// dialog element. Swapping the element means the browser sees a brand-new
// dialog being opened and replays the native modal transition on every single
// cart change — visible as the drawer closing and reopening.
const applyDrawerContent = (currentDrawer, newDrawer, keepOpen) => {
  // Carry the SAME bar element across the swap rather than letting
  // replaceChildren destroy it. This is what makes the fill animate: the
  // browser has already painted this element at its previous width, so setting
  // a new one transitions. Recreating it each render is what left the bar
  // permanently one step behind.
  const liveBar = currentDrawer.querySelector('[data-dw-shipping-bar]');
  if (liveBar) liveBar.remove(); // detached, but we keep the reference

  currentDrawer.replaceChildren(...newDrawer.childNodes);
  [...newDrawer.attributes].forEach((attr) => {
    if (attr.name === 'open') return; // never let markup override the live open state
    currentDrawer.setAttribute(attr.name, attr.value);
  });

  if (liveBar) {
    const header = currentDrawer.querySelector('.cart-drawer__header');
    const content = currentDrawer.querySelector('.cart-drawer__content');
    if (header) header.insertAdjacentElement('afterend', liveBar);
    else if (content) content.insertAdjacentElement('beforebegin', liveBar);
  }

  if (keepOpen && !currentDrawer.open) {
    currentDrawer.showModal();
    document.documentElement.classList.add('dw-cart-is-open');
  }
};

const refreshCartDrawer = async (keepOpen = false, cartOverride = null) => {
  const [sectionsData, cart] = await Promise.all([
    fetch(`${window.location.pathname}?sections=dw-cart-drawer`).then((res) => res.json()),
    cartOverride || fetch('/cart.js').then((res) => res.json()),
  ]);
  const drawerHTML = sectionsData['dw-cart-drawer'];
  if (!drawerHTML) return;

  const doc = new DOMParser().parseFromString(drawerHTML, 'text/html');
  const newDrawer = doc.querySelector('[data-dw-cart-overlay]');
  if (!newDrawer) return;

  syncDrawerState(newDrawer);

  const currentDrawer = getDrawer();
  if (currentDrawer) {
    applyDrawerContent(currentDrawer, newDrawer, keepOpen);
    // After the swap, on the live element — see syncShippingProgressBar.
    syncShippingProgressBar(currentDrawer, cart);
  }
};

// Removes every line matching a key, one at a time. Sequential on purpose:
// Shopify's cart is not safe for concurrent writes, and firing the removals
// together returns a 400 for whichever requests race the structure change.
const removeLinesMatching = async (cart, key) => {
  let current = cart;
  let remaining = current.items.filter((item) => lineKeyFor(item) === key);
  let guard = remaining.length + 3;

  while (remaining.length) {
    if (guard-- <= 0) throw new Error('Cart remove did not converge');
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: remaining[0].key, quantity: 0 }),
    });
    if (!response.ok) throw new Error('Cart remove failed');
    current = await response.json();
    remaining = current.items.filter((item) => lineKeyFor(item) === key);
  }

  return current;
};

document.addEventListener('DOMContentLoaded', () => {
  // Items already in the cart on a plain page load need the same sync as ones
  // added during the session — the subscription cap and the shipping bar are
  // both client-side and neither exists in the server's markup.
  (async () => {
    const cart = await fetch('/cart.js').then((res) => res.json());
    syncDrawerState(document);
    syncShippingProgressBar(document, cart);
  })();

  updateCartCount();

  // ─── Open / close ─────────────────────────────────────────

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-dw-cart-open]')) openCart();
    if (event.target.closest('[data-dw-cart-close]')) closeCart();

    const drawer = getDrawer();
    if (drawer && event.target === drawer) closeCart();
  });

  // ?open_cart=1 pops the drawer on arrival — used by the Klaviyo
  // abandoned-checkout email's fallback link, which can only point at the
  // storefront root when no checkout_url is available.
  if (new URLSearchParams(window.location.search).get('open_cart') === '1') {
    openCart();
  }

  // ─── Add to cart ──────────────────────────────────────────

  document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
    form.addEventListener(
      'submit',
      async (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const submitButton = form.querySelector('[type="submit"]');
        if (submitButton) submitButton.disabled = true;

        try {
          const formData = new FormData(form);
          const purchaseType = form.querySelector('input[name="purchase_type"]:checked')?.value;
          const wrapper = form.querySelector('.dw-purchase-options');

          // The buy box keeps the chosen variant and plan on the wrapper's
          // dataset, rewritten on every selection change.
          if (wrapper) {
            const variantId = wrapper.dataset.subscriptionVariantId;
            if (variantId) formData.set('id', variantId);
          }

          if (purchaseType === 'subscription') {
            const planId = wrapper?.dataset.subscriptionSellingPlanId;
            if (planId) formData.set('selling_plan', planId);
            // Selling-plan data does not reliably survive into the checkout
            // webhook Klaviyo reads, but line-item properties do — this is
            // what the abandoned-cart email keys its layout off.
            formData.append('properties[Subscription]', 'true');
          } else {
            formData.delete('selling_plan');
          }

          formData.set('sections', 'dw-cart-drawer');
          formData.set('sections_url', window.location.pathname);

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData,
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) throw new Error('Cart add failed');

          const data = await response.json();
          const drawerHTML = data.sections?.['dw-cart-drawer'];

          if (drawerHTML) {
            const cart = await fetch('/cart.js').then((res) => res.json());
            const doc = new DOMParser().parseFromString(drawerHTML, 'text/html');
            const newDrawer = doc.querySelector('[data-dw-cart-overlay]');

            if (newDrawer) {
              syncDrawerState(newDrawer);
              const currentDrawer = getDrawer();
              if (currentDrawer) {
                applyDrawerContent(currentDrawer, newDrawer, false);
                syncShippingProgressBar(currentDrawer, cart);
              }
            }
          }

          await updateCartCount();
          openCart();
        } catch (error) {
          console.error(error);
        } finally {
          if (submitButton) submitButton.disabled = false;
        }
      },
      true
    );
  });

  // ─── Remove from cart ─────────────────────────────────────

  document.addEventListener('click', async (event) => {
    const deleteBtn = event.target.closest('[data-dw-cart-remove]');
    if (!deleteBtn) return;

    const lineIndex = deleteBtn.dataset.dwCartRemove;
    if (!lineIndex) return;

    deleteBtn.disabled = true;

    try {
      const cart = await fetch('/cart.js').then((res) => res.json());
      const clickedItem = cart.items[parseInt(lineIndex, 10) - 1];
      if (!clickedItem) throw new Error('Could not resolve cart line to remove');

      const updated = await removeLinesMatching(cart, lineKeyFor(clickedItem));

      await refreshCartDrawer(true, updated);
      await updateCartCount();
    } catch (error) {
      console.error('Remove failed:', error);
      deleteBtn.disabled = false;
    }
  });

  // ─── Purchase option picker ───────────────────────────────
  //
  // The <select> in each row now covers BOTH axes — which supply size, and
  // one-time vs which schedule — grouped into two <optgroup>s by
  // cart-products.liquid. Values are:
  //     onetime:<variantId>
  //     subscribe:<variantId>:<planId>
  //
  // Every change is a remove-then-add rather than a /cart/change.js patch.
  // That's deliberate: a change can now alter the variant, not just the
  // selling plan, and /cart/change.js cannot move a line to a different
  // variant at all. A fresh add is also the same code path the product page
  // already uses, so there's one add flow to keep working instead of two.
  //
  // Quantity always lands on 1. Switching purchase option is choosing a
  // different thing, not adjusting how many — carrying "6" across from a
  // previous selection is how the old cart ended up showing quantities
  // nobody asked for.

  const processingLines = new Set();

  // Clicking the visible button opens the native menu underneath it.
  document.addEventListener('click', (event) => {
    const frequencyBtn = event.target.closest('[data-dw-frequency-trigger]');
    if (!frequencyBtn) return;
    const select = frequencyBtn.parentElement.querySelector('[data-dw-frequency-select]');
    if (select) select.focus();
  });

  document.addEventListener('change', async (event) => {
    const select = event.target.closest('[data-dw-frequency-select]');
    if (!select) return;

    const row = select.closest('[data-line]');
    const lineIndex = row ? parseInt(row.dataset.line, 10) : NaN;
    if (!Number.isInteger(lineIndex)) return;
    if (processingLines.has(lineIndex)) return;
    processingLines.add(lineIndex);

    const btnLabel = row.querySelector('[data-dw-frequency-trigger] span');
    const chosenOption = select.options[select.selectedIndex];
    const chosenLabel = chosenOption ? chosenOption.textContent.trim() : '';

    // Roll back to whichever option the server marked selected if the change
    // fails. `defaultSelected` is the original attribute, not the live state.
    const previousValue =
      select.dataset.dwPreviousValue ??
      [...select.options].find((option) => option.defaultSelected)?.value;

    select.disabled = true;
    if (btnLabel) btnLabel.textContent = 'Updating…';

    try {
      const [kind, variantId, planId] = select.value.split(':');
      if (!variantId) throw new Error('Unrecognised purchase option value');

      let cart = await fetch('/cart.js').then((res) => res.json());
      const clickedItem = cart.items[lineIndex - 1];
      if (!clickedItem) throw new Error('Could not resolve cart line to change');

      cart = await removeLinesMatching(cart, lineKeyFor(clickedItem));

      const body = { items: [{ id: Number(variantId), quantity: 1 }] };
      if (kind === 'subscribe' && planId) {
        body.items[0].selling_plan = Number(planId);
        body.items[0].properties = { Subscription: 'true' };
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.description || 'Purchase option change failed');
      }

      cart = await fetch('/cart.js').then((res) => res.json());

      if (btnLabel) btnLabel.textContent = chosenLabel;
      select.dataset.dwPreviousValue = select.value;

      // Full re-render: the line's label, price, strikethrough and selected
      // option all come back from the server already correct.
      await refreshCartDrawer(true, cart);
      await updateCartCount();
    } catch (err) {
      console.error('Purchase option change error:', err);
      if (previousValue !== undefined) select.value = previousValue;
      const restoredOption = select.options[select.selectedIndex];
      if (btnLabel && restoredOption) btnLabel.textContent = restoredOption.textContent.trim();
    } finally {
      select.disabled = false;
      syncQuantityPlusState(row);
      processingLines.delete(lineIndex);
    }
  });
});

// ─── Quantity picker ────────────────────────────────────────
//
// Delegated on `document` so it survives the drawer being re-rendered. It
// therefore also sees clicks on the product page's own picker, which has its
// own listener in dw-quantity-picker.js — the `[data-line]` check below is
// what keeps those from being processed twice.
//
// Steps by 1 again. Under Bundler a "3-Month starter" was three cart units,
// so this had to step by a whole pack and display packs rather than units; a
// 3-month supply is one unit of one variant now.
let cartRequestToken = 0;

document.addEventListener('click', async (event) => {
  const minus = event.target.closest('[data-quantity-minus]');
  const plus = event.target.closest('[data-quantity-plus]');
  if (!minus && !plus) return;

  const picker = (minus || plus).closest('[data-quantity-picker]');
  if (!picker) return;

  const row = picker.closest('[data-line]');
  if (!row) return; // not a cart row — the product page's picker handles itself

  // Hard stop as well as the disabled attribute: this is delegated, and the
  // disabled state is only re-synced on a render.
  if (plus && isSubscriptionRow(row)) return;

  const value = picker.querySelector('[data-quantity-value]');
  const input = picker.querySelector('[data-quantity-input]');
  const lineIndex = parseInt(row.dataset.line, 10);
  if (!Number.isInteger(lineIndex)) return;

  // Optimistic, purely so the number moves on click; the re-render below
  // replaces it with whatever Shopify actually did.
  let optimistic = parseInt(input.value, 10) || 1;
  if (minus) {
    if (optimistic > 1) optimistic -= 1;
  } else {
    optimistic += 1;
  }
  input.value = optimistic;
  value.textContent = optimistic;

  const requestId = ++cartRequestToken;

  try {
    const cart = await fetch('/cart.js').then((res) => res.json());
    const clickedItem = cart.items[lineIndex - 1];
    if (!clickedItem) return;

    if (minus && clickedItem.quantity <= 1) {
      input.value = clickedItem.quantity;
      value.textContent = clickedItem.quantity;
      return;
    }

    const newQty = clickedItem.quantity + (plus ? 1 : -1);

    const changeResponse = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: clickedItem.key, quantity: newQty }),
    });

    // A newer click already started — let its render win instead of this one.
    if (requestId !== cartRequestToken) return;

    const changedCart = changeResponse.ok ? await changeResponse.json() : null;
    await refreshCartDrawer(true, changedCart);
    await updateCartCount();
  } catch (err) {
    console.error('Quantity update failed:', err);
  }
});